import { useState, useRef, KeyboardEvent } from 'react';
import { Box, Button, FormControl, OutlinedInput, InputAdornment, CircularProgress, Tooltip } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import { getMembers } from 'src/shared/services/memberService';
import { useTranslation } from 'react-i18next';

type MemberNumberSearchProps = {
  onSelect: (memberId: number) => void;
};

export const MemberNumberSearch = (props: MemberNumberSearchProps) => {
  const { onSelect } = props;
  const { t } = useTranslation(['common']);
  const [value, setValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [focused, setFocused] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleSearch = async () => {
    const trimmed = value.trim();
    if (!trimmed) return;

    setLoading(true);
    setNotFound(false);
    try {
      const result = await getMembers(0, 1, 'memberNr', 'asc', { memberNr: trimmed } as any, [], '');
      const match = result.data.results?.[0];
      if (match?.member_id) {
        onSelect(match.member_id);
        setValue('');
      } else {
        setNotFound(true);
      }
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch();
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: '100%' }}>
      <Tooltip title={notFound ? t('common:form.memberNotFound') : t('common:form.memberNumberTooltip')} open={focused || notFound} arrow>
        <FormControl
          variant='outlined'
          size='small'
          fullWidth
          sx={{
            '& .MuiInputBase-root': {
              backgroundColor: '#fff',
            },
            '& .MuiInputBase-input::placeholder': {
              color: '#666',
              opacity: 1,
            },
            '& .MuiInputAdornment-root': {
              color: 'grey.400',
            },
          }}
        >
          <OutlinedInput
            inputRef={inputRef}
            type='text'
            value={value}
            placeholder={t('common:form.memberNumberPlaceholder')}
            startAdornment={
              <InputAdornment position='start'>
                <PersonIcon />
              </InputAdornment>
            }
            error={notFound}
            onChange={(e) => {
              setValue(e.target.value);
              setNotFound(false);
            }}
            onKeyDown={handleKeyDown}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
          />
        </FormControl>
      </Tooltip>
      <Button variant='contained' size='small' onClick={handleSearch} disabled={!value.trim() || loading} sx={{ height: '40px' }}>
        {loading ? <CircularProgress size={18} color='inherit' /> : t('common:button.show')}
      </Button>
    </Box>
  );
};
